import React, { FC } from "react";
import styled, { css } from "styled-components";
import { createMarkup } from "../utils/createMarkup";
import { FooterLogo } from "./FooterLogo";
import { Section } from "./Section";
import { Spacer } from "./Spacer";
import { SslSecure } from "./svg/SslSecure";
import {
  FooterFacebook as Facebook,
  FooterInstagram as Instagram,
  FooterLinkedIn as LinkedIn,
  FooterTwitter as Twitter,
} from "./icons";
import { Typography } from "./Typography";
import { ButtonPinReveal } from "./ButtonPinReveal";

// Footer data copy-pasted from agria-frontend gatsby-config
const data = {
  footerNavigationColumns: [
    {
      links: [
        {
          label: "Contact",
          url: "https://www.agriapet.co.uk/contact/",
        },
        {
          label: "About us",
          url: "https://www.agriapet.co.uk/about-us/",
        },
        {
          label: "Blog",
          url: "https://www.agriapet.co.uk/blog/",
        },
      ],
    },
    {
      links: [
        {
          label: "Sustainability",
          url: "https://www.agriapet.co.uk/sustainability/",
        },
        {
          label: "Agria app",
          url: "https://www.agriapet.co.uk/agria-app/",
        },
        {
          label: "Accessibility",
          url: "https://www.agriapet.co.uk/accessibility/",
        },
      ],
    },
    {
      links: [
        {
          label: "Making a complaint",
          url: "https://www.agriapet.co.uk/making-a-complaint/",
        },
        {
          label: "Press Enquiries",
          url: "https://www.agriapet.co.uk/press-enquiries/",
        },
        {
          label: "Careers",
          url: "https://www.agriapet.co.uk/careers/",
        },
      ],
    },
    {
      links: [
        {
          label: "Join our Affiliate Programme",
          url: "https://www.agriapet.co.uk/affiliate-programme/",
          target: "_blank",
        },
        {
          label: "Become a Partner",
          url: "https://www.agriapet.co.uk/become-a-partner/",
        },
      ],
    },
  ],
  footerSecondaryNavigation: [
    {
      url: "https://www.agriapet.co.uk/cookie-policy/",
      label: "Cookie policy",
    },
    {
      url: "https://www.agriapet.co.uk/privacy-policy/",
      label: "Privacy policy",
    },
    {
      url: "https://www.agriapet.co.uk/uk-tax-policy/",
      label: "UK tax policy",
    },
    {
      url: "https://www.agriapet.co.uk/terms-and-conditions/",
      label: "Terms and conditions",
    },
    {
      url: "https://www.agriapet.co.uk/modern-slavery-statement-policy/",
      label: "Modern slavery statement",
    },
  ],
  footerSocialLinks: {
    facebook: "https://www.facebook.com/agriapetuk/",
    twitter: "https://twitter.com/AgriaPetUK",
    linkedIn: "https://uk.linkedin.com/company/agria-pet-insurance",
    instagram: "https://www.instagram.com/agriapetinsurance/",
  },
  footerTerms: `
    <p>
      Trustpilot and Defaqto ratings correct at time of print. Agria Pet Insurance has been awarded 'Best Pet Insurance Overall' by The Telegraph. Terms, conditions and excesses apply. Promotions may be withdrawn at any time without notice. Vet fees reinstated each year provided you renew your policy each year without a break in cover. 
    </p>
    <p>
      For UK customers:
      <br />
      Agria Pet Insurance Ltd is authorised and regulated by the Financial Conduct Authority, Financial Services Register Number 496160. Agria Pet Insurance Ltd is registered and incorporated in England and Wales with registered number 04258783. Registered office: First Floor, Blue Leanie, Walton Street, Aylesbury, Buckinghamshire, HP21 7QW. Agria insurance policies are underwritten by Agria Forsakring who is authorised and regulated by the Prudential Regulatory Authority and Financial Conduct Authority.
    </p>
    <p>
      For Jersey customers:
      <br />
      Agria Pet Insurance Ltd is regulated by the Jersey Financial Services Commission (JFSC). Ref: 0001498. Registered office: As detailed above.
    </p>
    <p>
      For Guernsey customers:
      <br />
      Clegg Gifford Channel Islands Limited is licensed by the Guernsey Financial Services Commission. Ref: 2722221. Registered office: Admiral House, Place Du Commerce, St Peter Port, Guernsey GY1 2AT.
    </p>
    <p>
      © 2025 Agria Pet Insurance Ltd. All Rights Reserved.
    </p>
  `,
};

interface FooterProps {}

const StyledFooter = styled.footer`
  ${({ theme: { components, colors } }) => css`
    background: ${components.footer.background};
    color: #ccd5de;

    a:focus-visible,
    button:focus-visible {
      outline: 2px solid ${colors.white.default};
      outline-offset: 2px;
    }
  `};
`;

const StyledSmallText = styled(Typography)`
  ${({ theme: { space } }) => css`
    p + p {
      margin-top: ${space.sm};
    }
  `};
`;

const StyledSocialLinks = styled.ul`
  ${({ theme: { space, colors, components } }) => css`
    display: flex;
    gap: ${space.xxs};

    a {
      display: inline-block;

      &:focus-visible {
        color: ${colors.white.default};
        outline: none;
        box-shadow:
          0px 0px 0px 2px ${colors.white.default},
          0px 0px 0px 4px ${components.footer.background};
      }
    }
  `};
`;

const StyledSmallLinks = styled.ul`
  ${({ theme: { space, media, colors } }) => css`
    display: flex;
    flex-direction: column;
    gap: ${space.xs};

    a,
    button {
      text-decoration: underline;

      &:hover,
      &:focus-visible {
        text-decoration: underline;
        color: ${colors.white.default};
      }
    }

    @media (min-width: ${media.lg}) {
      flex-direction: row;
      gap: ${space.md};

      li + li {
        border-left: solid 1px #345a7b;
        padding-left: ${space.md};
      }
    }
  `};
`;

const StyledDiv = styled.div`
  ${({ theme: { space, maxMedia } }) => css`
    display: flex;
    row-gap: ${space.lg};

    @media (max-width: ${maxMedia.lg}) {
      flex-direction: column;
    }
  `};
`;

const StyledMenus = styled.div`
  ${({ theme: { space, media, colors } }) => css`
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    gap: ${space.sm};

    @media (min-width: ${media.lg}) {
      flex-direction: row;
      justify-content: space-between;

      &:after {
        content: "";
      }
    }

    a,
    button {
      &:hover,
      &:focus-visible {
        color: ${colors.white.default};
      }
    }
  `};
`;

const StyledIcon = styled.div`
  svg,
  img {
    width: 48px;
    height: 48px;
  }
`;

const StyledSection = styled(Section)`
  ${({ theme: { space, media } }) => css`
    padding: ${space.xxxl} 0;

    @media (min-width: ${media.lg}) {
      padding: ${space.xxxxl} 0;
    }
  `};
`;

const StyledLogos = styled.div`
  ${({ theme: { colors, space } }) => css`
    display: flex;
    flex-shrink: 0;
    flex-direction: column;
    gap: ${space.sm};
    align-items: flex-start;
    color: ${colors.white.default};
  `};
`;

const StyledDivider = styled.hr`
  ${({ theme: { space } }) => css`
    margin-top: ${space.xl};
    border: none; /* Reset all borders */
    border-bottom: 1px solid #8098ad;
  `};
`;

const StyledNavigationColumn = styled.div`
  ${({ theme: { media, space } }) => css`
    display: flex;
    flex-direction: column;
    gap: ${space.sm};

    a {
      text-decoration: none;
    }

    @media (min-width: ${media.lg}) {
      gap: ${space.md};
    }
  `};
`;

export const Footer: FC<FooterProps> = () => (
  <StyledFooter>
    <StyledSection background="darkBlue">
      <Spacer spacing="double">
        <FooterLogo />

        <StyledDivider />

        <StyledDiv as="nav" aria-label="Footer Navigation Menu">
          <StyledMenus role="list">
            {data.footerNavigationColumns.map((navColumn, index) => (
              <StyledNavigationColumn key={index}>
                {navColumn.links?.map(({ label, url }, index) =>
                  url ?
                    <StyledDiv key={index} role="listitem">
                      <a href={url}>{label}</a>
                    </StyledDiv>
                  : null,
                )}
              </StyledNavigationColumn>
            ))}
          </StyledMenus>

          <StyledLogos>
            <Typography component="h3" variant="paragraphBold">
              Follow us
            </Typography>

            <StyledSocialLinks>
              <li>
                <a
                  href={data.footerSocialLinks.facebook}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Facebook"
                >
                  <StyledIcon>
                    <Facebook aria-hidden="true" />
                  </StyledIcon>
                </a>
              </li>
              <li>
                <a
                  href={data.footerSocialLinks.twitter}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Twitter"
                >
                  <StyledIcon>
                    <Twitter aria-hidden="true" />
                  </StyledIcon>
                </a>
              </li>
              <li>
                <a
                  href={data.footerSocialLinks.linkedIn}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="LinkedIn"
                >
                  <StyledIcon>
                    <LinkedIn aria-hidden="true" />
                  </StyledIcon>
                </a>
              </li>
              <li>
                <a
                  href={data.footerSocialLinks.instagram}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Instagram"
                >
                  <StyledIcon>
                    <Instagram aria-hidden="true" />
                  </StyledIcon>
                </a>
              </li>
            </StyledSocialLinks>
          </StyledLogos>
        </StyledDiv>

        <StyledDivider />

        <StyledDiv as="nav" aria-label="Policies and Cookies Navigation Menu">
          <StyledSmallLinks as="ul">
            {data.footerSecondaryNavigation.map((nav, index) =>
              nav.url ?
                <Typography component="li" variant="small" key={index}>
                  <a href={nav.url}>{nav.label}</a>
                </Typography>
              : null,
            )}
          </StyledSmallLinks>
        </StyledDiv>

        <StyledSmallText variant="small" component="div">
          {createMarkup(data.footerTerms)}
        </StyledSmallText>
        <SslSecure title="SSL secure payment" />
        {process.env.GATSBY_LOAD_SURFLY === "true" && <ButtonPinReveal />}
      </Spacer>
    </StyledSection>
  </StyledFooter>
);

export default Footer;
